/**
 * Circuit breaker around "restart the login flow" redirects.
 *
 * If the condition that caused a restart persists, Auth0's SSO cookie
 * auto-approves and the app redirect-loops so fast the user can't even open
 * DevTools. Allow one restart per window; suppress repeats so the underlying
 * error surfaces instead. No page-load path bounces through Auth0 any more —
 * an unauthenticated load and a session lost mid-use both land on the app's
 * own login page (see sessionEnded.ts) — so this now only guards restarts
 * a person triggers (AuthErrorGate's retry), where the reset on logout keeps
 * it out of the way.
 */
const KEY = 'auth-restart-login-at';
const WINDOW_MS = 30_000;

/**
 * Redirecting a hidden tab parks it on Auth0's Universal Login page, whose
 * server-side transaction expires while the tab sits there — the user comes
 * back (e.g. opens their laptop in the morning) to Auth0's "invalid_request:
 * we couldn't find your session" page. Defer the restart until the tab is
 * visible so the login transaction is always fresh when the user sees it.
 * Only the most recent deferred restart runs.
 */
let pendingRestart: (() => void) | null = null;

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !pendingRestart) return;
    const restart = pendingRestart;
    pendingRestart = null;
    guardedRestartLogin(restart);
});

/**
 * A user-initiated logout is never a redirect loop, but the post-logout
 * landing restarts login and would trip the breaker if the user had logged
 * in within the window (log in → immediately log out → blank page). The
 * 401-loop this guard exists for never passes through logout, so resetting
 * here keeps the breaker intact where it matters.
 */
export function resetRestartLoginGuard(): void {
    pendingRestart = null;
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        // ignore
    }
}

export function guardedRestartLogin(restart: () => void): void {
    if (document.visibilityState === 'hidden') {
        pendingRestart = restart;
        return;
    }
    let last = 0;
    try {
        last = Number(sessionStorage.getItem(KEY) ?? 0);
    } catch {
        // sessionStorage unavailable — fall through and allow the restart.
    }
    if (Date.now() - last < WINDOW_MS) {
        console.error(
            `[auth] Login restart suppressed — already restarted within ${
                WINDOW_MS / 1000
            }s. The app is redirect-looping; the real failure is whatever ` +
                'triggered this (check the Network tab for a 401 or a failed ' +
                '/oauth/token call).'
        );
        return;
    }
    try {
        sessionStorage.setItem(KEY, String(Date.now()));
    } catch {
        // ignore
    }
    restart();
}
